import * as React from "react";

import { cn } from "~/lib/utils";

// AIDEV-NOTE: Eyebrow — print-design term for the small uppercase label that
// sits above a headline ("OPINION", "SCIENCE"). In product UI it orients the
// reader and names the section without competing with the H1 for weight.
//
// Wraps the `.u-eyebrow` CSS utility (Inter 11/600/uppercase/.35px tracking,
// forest-700 by default). Use this component everywhere you'd otherwise hand-
// roll `text-[11px] font-semibold uppercase tracking-[…]` or the older 10/12px
// drift variants. See DESIGN.md → `utilities.u-eyebrow`.
//
// Do NOT use inside body sentences.

const TONE_CLASS: Record<EyebrowTone, string> = {
    forest: "text-brand-forest-700",
    terra: "text-brand-terra-600",
    muted: "text-brand-forest-500",
    inherit: "text-inherit",
};

export type EyebrowTone = "forest" | "terra" | "muted" | "inherit";

export interface EyebrowProps extends React.HTMLAttributes<HTMLSpanElement> {
    /** Render-as element. Defaults to `span` so the eyebrow doesn't compete
     *  with the heading for landmark semantics. Use `"div"` when the eyebrow
     *  needs block layout (e.g., margin-bottom above an H1). */
    as?: "span" | "div" | "p";
    /** Text color tone. Defaults to `forest` (forest-700). */
    tone?: EyebrowTone;
}

const Eyebrow = React.forwardRef<HTMLElement, EyebrowProps>(
    (
        { as: Tag = "span", tone = "forest", className, children, ...props },
        ref,
    ) => {
        // AIDEV-NOTE: Cast Tag to a string element so TS uses the union ref
        // shape (HTMLElement) instead of trying to narrow per-tag. Acceptable
        // here because all three options (span/div/p) extend HTMLElement.
        const Component = Tag as "span";
        return (
            <Component
                ref={ref as React.Ref<HTMLSpanElement>}
                className={cn("u-eyebrow", TONE_CLASS[tone], className)}
                {...props}
            >
                {children}
            </Component>
        );
    },
);
Eyebrow.displayName = "Eyebrow";

export { Eyebrow };
